import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { disableMfa, enableMfa, getMfaStatus, setupMfa } from "./api";
import type { MfaSetup } from "./types";

export function MfaSettingsPanel() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    getMfaStatus()
      .then((status) => setEnabled(status.enabled))
      .catch((requestError) =>
        setError(requestError instanceof Error ? requestError.message : "Failed to load MFA status"),
      )
      .finally(() => setLoading(false));
  }, []);

  async function startSetup() {
    setError(null);
    setPending(true);
    try {
      const result = await setupMfa();
      setSetup(result);
      setCode("");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Failed to start MFA setup");
    } finally {
      setPending(false);
    }
  }

  async function confirmEnable() {
    setError(null);
    setPending(true);
    try {
      const status = await enableMfa(code.trim());
      setEnabled(status.enabled);
      setSetup(null);
      setCode("");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Invalid verification code");
    } finally {
      setPending(false);
    }
  }

  async function confirmDisable() {
    setError(null);
    setPending(true);
    try {
      const status = await disableMfa(code.trim());
      setEnabled(status.enabled);
      setCode("");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Invalid verification code");
    } finally {
      setPending(false);
    }
  }

  return (
    <article className="panel mfa-settings-panel">
      <header className="panel-header">
        <h2>Two-Factor Authentication</h2>
        <span>{loading ? "Loading..." : enabled ? "Enabled" : "Disabled"}</span>
      </header>
      <p className="management-note">
        Protect your account with a time-based code from an authenticator app, in addition to
        your password.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      {loading ? null : enabled && !setup ? (
        <div className="action-form action-card">
          <label htmlFor="mfa-disable-code">Enter a current code to disable MFA</label>
          <div className="checkin-fields">
            <input
              id="mfa-disable-code"
              inputMode="numeric"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
            />
            <button
              className="refresh-btn danger-action-btn"
              type="button"
              disabled={pending || code.trim().length !== 6}
              onClick={() => void confirmDisable()}
            >
              {pending ? "Disabling..." : "Disable MFA"}
            </button>
          </div>
        </div>
      ) : setup ? (
        <div className="action-form action-card">
          <p>Scan this QR code with your authenticator app, then enter the code it shows.</p>
          <div className="qr-panel">
            <QRCodeSVG value={setup.otpauth_url} size={160} />
            <code>{setup.secret}</code>
          </div>
          <label htmlFor="mfa-enable-code">Confirmation Code</label>
          <div className="checkin-fields">
            <input
              id="mfa-enable-code"
              inputMode="numeric"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              autoFocus
            />
            <button
              className="refresh-btn action-submit"
              type="button"
              disabled={pending || code.trim().length !== 6}
              onClick={() => void confirmEnable()}
            >
              {pending ? "Enabling..." : "Confirm & Enable"}
            </button>
            <button
              className="refresh-btn"
              type="button"
              disabled={pending}
              onClick={() => {
                setSetup(null);
                setCode("");
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="refresh-btn action-submit" type="button" disabled={pending} onClick={() => void startSetup()}>
          {pending ? "Starting..." : "Enable MFA"}
        </button>
      )}
    </article>
  );
}
